// frontend/src/quiz_management/performancereport.js
// UPDATED: Show only accessible subjects for the student
import React, { useState, useEffect } from 'react';
import './performancereport.css';
import API_BASE_URL from './config';
import { API_BASE, authFetch } from '../services/api.js';
import CustomSelect from '../components/CustomSelect';

const PerformanceReport = ({ studentId }) => {
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedChapter, setSelectedChapter] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadSubjects();
    }, [studentId]);

    useEffect(() => {
        if (selectedSubject) {
            loadChapters(selectedSubject);
            setSelectedChapter('');
            setSelectedTopic('');
        }
    }, [selectedSubject]);

    useEffect(() => {
        if (selectedChapter) {
            loadTopics(selectedChapter);
            setSelectedTopic('');
        }
    }, [selectedChapter]);

    useEffect(() => {
        if (selectedSubject) {
            loadReportData();
        }
    }, [selectedSubject, selectedChapter, selectedTopic, studentId]);

    const loadSubjects = async () => {
        try {
            // UPDATED: Load only accessible subjects for this student
            const response = await authFetch(`${API_BASE_URL}/subjects/?student_id=${studentId}`);
            const data = await response.json();
            setSubjects(data || []);
        } catch (err) {
            console.error('Error loading accessible subjects:', err);
            setSubjects([]);
        }
    };

    const loadChapters = async (subjectId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/chapters/subject/${subjectId}?student_id=${studentId}`);
            const data = await response.json();
            setChapters(data);
        } catch (err) {
            console.error('Error loading chapters:', err);
            setChapters([]);
        }
    };

    const loadTopics = async (chapterId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/topics/chapter/${chapterId}?student_id=${studentId}`);
            const data = await response.json();
            setTopics(data);
        } catch (err) {
            console.error('Error loading topics:', err);
            setTopics([]);
        }
    };

    const loadReportData = async () => {
        if (!selectedSubject) return;
        
        setLoading(true);
        setError(null);
        
        try {
            let endpoint = '';
            let params = new URLSearchParams({ student_id: studentId });
            
            if (selectedTopic) {
                // Show subtopic-wise performance
                endpoint = `${API_BASE_URL}/quiz/report/performance/topic/${selectedTopic}`;
            } else if (selectedChapter) {
                // Show topic-wise performance
                endpoint = `${API_BASE_URL}/quiz/report/performance/chapter/${selectedChapter}`;
            } else {
                // Show chapter-wise performance
                endpoint = `${API_BASE_URL}/quiz/report/performance/subject/${selectedSubject}`;
            }
            
            const response = await authFetch(`${endpoint}?${params}`);
            if (!response.ok) throw new Error('Failed to load report data');
            
            const data = await response.json();
            setReportData(data.details || []);
            setSummaryData(data.summary || null);
            
        } catch (err) {
            setError('Failed to load performance report. Please try again.');
            console.error('Error loading report:', err);
        } finally {
            setLoading(false);
        }
    };

    const getPerformanceColor = (percentage) => {
        if (percentage >= 80) return 'high-performance';
        if (percentage >= 60) return 'medium-performance';
        return 'low-performance';
    };

    const getReportLevel = () => {
        if (selectedTopic) return 'Subtopic';
        if (selectedChapter) return 'Topic';
        return 'Chapter';
    };

    const getSelectedName = () => {
        if (selectedTopic) {
            const topic = topics.find(t => t.topic_id == selectedTopic);
            return topic?.name || 'Selected Topic';
        }
        if (selectedChapter) {
            const chapter = chapters.find(c => c.chapter_id == selectedChapter);
            return chapter?.name || 'Selected Chapter';
        }
        const subject = subjects.find(s => s.subject_id == selectedSubject);
        return subject?.name || 'Selected Subject';
    };

    return (
        <div className="performance-report-container">
            <div className="report-header">
                <h2>Performance Report</h2>
                <p>Analyze your quiz performance across subjects, chapters, and topics</p>
            </div>

            {/* Filter Controls */}
            <div className="filter-controls">
                <div className="filter-row">
                    <div className="filter-group">
                        <label htmlFor="subject-select">Subject *</label>
                        <CustomSelect
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            className="filter-select"
                            placeholder="Select Subject"
                            options={subjects.map(s => ({ value: s.subject_id, label: s.name }))}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="chapter-select">Chapter</label>
                        <CustomSelect
                            value={selectedChapter}
                            onChange={(e) => setSelectedChapter(e.target.value)}
                            className="filter-select"
                            disabled={!selectedSubject}
                            placeholder="All Chapters"
                            options={[
                                { value: '', label: 'All Chapters' },
                                ...chapters.map(c => ({ value: c.chapter_id, label: c.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="topic-select">Topic</label>
                        <CustomSelect
                            value={selectedTopic}
                            onChange={(e) => setSelectedTopic(e.target.value)}
                            className="filter-select"
                            disabled={!selectedChapter}
                            placeholder="All Topics"
                            options={[
                                { value: '', label: 'All Topics' },
                                ...topics.map(t => ({ value: t.topic_id, label: t.name }))
                            ]}
                        />
                    </div>
                </div>
            </div>

            {selectedSubject && (
                <>
                    {/* Summary Section */}
                    {summaryData && (
                        <div className="summary-section">
                            <h3>{getSelectedName()} - Overall Performance</h3>
                            <div className="summary-cards">
                                <div className="summary-card">
                                    <div className="summary-number">{summaryData.total_quizzes || 0}</div>
                                    <div className="summary-label">Total Quizzes</div>
                                </div>
                                <div className="summary-card">
                                    <div className="summary-number">{summaryData.attempted_quizzes || 0}</div>
                                    <div className="summary-label">Attempted</div>
                                </div>
                                <div className="summary-card">
                                    <div className="summary-number">{summaryData.pending_quizzes || 0}</div>
                                    <div className="summary-label">Pending</div>
                                </div>
                                <div className="summary-card">
                                    <div className="summary-number">{summaryData.total_attempts || 0}</div>
                                    <div className="summary-label">Total Attempts</div>
                                </div>
                                <div className="summary-card">
                                    <div className={`summary-number ${getPerformanceColor(summaryData.overall_percentage || 0)}`}>
                                        {(summaryData.overall_percentage || 0).toFixed(1)}%
                                    </div>
                                    <div className="summary-label">Overall Score</div>
                                </div>
                                <div className="summary-card">
                                    <div className={`summary-number ${getPerformanceColor(summaryData.latest_percentage || 0)}`}>
                                        {(summaryData.latest_percentage || 0).toFixed(1)}%
                                    </div>
                                    <div className="summary-label">Latest Score</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Report Table */}
                    <div className="report-section">
                        <h3>{getReportLevel()}-wise Performance</h3>
                        
                        {loading && (
                            <div className="loading-spinner">Loading report data...</div>
                        )}

                        {error && (
                            <div className="error-message">{error}</div>
                        )}

                        {!loading && !error && reportData.length === 0 && (
                            <div className="no-data-message">
                                No quiz data available for the selected criteria.
                            </div>
                        )}

                        {!loading && !error && reportData.length > 0 && (
                            <div className="report-table-container">
                                <table className="report-table">
                                    <thead>
                                        <tr>
                                            <th>{getReportLevel()} Name</th>
                                            <th>Total Quizzes</th>
                                            <th>Attempted</th>
                                            <th>Pending</th>
                                            <th>Times Attempted</th>
                                            <th>% Score (All)</th>
                                            <th>% Score (Latest)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.map((row, index) => (
                                            <tr key={index}>
                                                <td className="name-cell">{row.name}</td>
                                                <td>{row.total_quizzes || 0}</td>
                                                <td>{row.attempted_quizzes || 0}</td>
                                                <td>{row.pending_quizzes || 0}</td>
                                                <td>{row.total_attempts || 0}</td>
                                                <td>
                                                    <span className={`score-cell ${getPerformanceColor(row.overall_percentage || 0)}`}>
                                                        {(row.overall_percentage || 0).toFixed(1)}%
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`score-cell ${getPerformanceColor(row.latest_percentage || 0)}`}>
                                                        {(row.latest_percentage || 0).toFixed(1)}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}

            {!selectedSubject && (
                <div className="select-subject-message">
                    <p>Please select a subject to view performance report</p>
                </div>
            )}
        </div>
    );
};

export default PerformanceReport;